import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Input, Button, Stack, Typography, Box, Grid } from '@mui/material';
import { getFunctions, httpsCallable } from "firebase/functions";
import { useUserContext } from '../../../../context/user_context/UserContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import BaseModal from '../../../../components/modals/BaseModal';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
const ReceiveOrderModal = ({ show, onHide, order, onReceive, inventoryItems }) => {
  const { firebaseUser } = useUserContext();
  const { setLoadingData } = useLoadingContext();
  const { t } = useTranslation();

  const initializeReceivedItems = () => (
    order.items
      ? Object.keys(order.items).map(itemId => ({
          inventory_item_id: itemId,
          quantity_received: 0,
        }))
      : []
  );

  const [receivedItems, setReceivedItems] = useState(initializeReceivedItems);

  useEffect(() => {
    if (show) {
      setReceivedItems(initializeReceivedItems());
    }
  }, [show, order]);

  const handleQuantityChange = (index, event) => {
    const updatedItems = [...receivedItems];
    const item = order.items[updatedItems[index].inventory_item_id];
    const maxReceivable = item.quantity_ordered - (item.quantity_received || 0);
    let newQuantity = parseInt(event.target.value, 10) || 0;

    if (newQuantity > maxReceivable) {
      newQuantity = maxReceivable;
    }

    updatedItems[index].quantity_received = newQuantity;
    setReceivedItems(updatedItems);
  };

  const handleReceiveOrder = async () => {
    setLoadingData(true);
    const organizationId = firebaseUser.organization;
    const updatedBy = firebaseUser.uid;
    
    const functions = getFunctions();
    const receiveOrderFn = httpsCallable(functions, CloudFunctions.ReceiveOrder);
    
    try {
      await receiveOrderFn({
        organizationId,
        orderId: order.id,
        receivedItems,
        updatedBy,
      });
      setLoadingData(false);
      onReceive(receivedItems);
      onHide(); 
    } catch (error) {
      setLoadingData(false);
      console.error('Error receiving order:', error);
    }
  };

  const getItemNameById = (itemId) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    return item ? item.name : 'Unknown Item';
  };

  const getItemImageById = (itemId) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    return item ? item.image_url : '';
  };

  const actions = (
    <>
      <Button variant="contained" color="primary" onClick={handleReceiveOrder}>
        Receive Items
      </Button>
    </>
  );

  return (
    <BaseModal
      open={show}
      onClose={onHide}
      title={`Receive Order #${order.id}`} 
      actions={actions}
      maxWidth="md"
    >
      <Stack spacing={3} sx={{ width: '100%' }}>
        {receivedItems.length > 0 ? (
          receivedItems.map((receivedItem, index) => {
            const item = order.items[receivedItem.inventory_item_id];
            const maxReceivable = item.quantity_ordered - (item.quantity_received || 0);
            const itemName = getItemNameById(receivedItem.inventory_item_id);
            const itemImageUrl = getItemImageById(receivedItem.inventory_item_id);
            const isFullyReceived = maxReceivable === 0;

            return (
              <Box
                key={receivedItem.inventory_item_id}
                sx={{
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Box
                      sx={{
                        width: '60px',
                        height: '60px',
                        borderRadius: 1,
                        overflow: 'hidden',
                        margin: { xs: '0 auto', sm: '0' },
                      }}
                    >
                      <img 
                        src={itemImageUrl} 
                        alt={itemName} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {itemName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(StringKeys.ORDERED)}: {item.quantity_ordered} {' '} • {' '} 
                      {t(StringKeys.RECEIVED)}: {item.quantity_received || 0} {' '} • {' '} 
                      {t(StringKeys.REMAINING)}: {maxReceivable}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {isFullyReceived ? (
                      <Typography 
                        variant="body2" 
                        color="success.main" 
                        sx={{ 
                          textAlign: { xs: 'center', sm: 'left' },
                          fontWeight: 'medium'
                        }}
                      >
                        ✓ {t(StringKeys.FULLY_RECEIVED)}
                      </Typography>
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel 
                          shrink 
                          htmlFor={`quantity-${receivedItem.inventory_item_id}`}
                        >
                          {t(StringKeys.RECEIVE_QUANTITY)}
                        </InputLabel>
                        <Input
                          id={`quantity-${receivedItem.inventory_item_id}`}
                          type="number"
                          value={receivedItems[index].quantity_received}
                          onChange={(e) => handleQuantityChange(index, e)}
                          inputProps={{ 
                            min: 0, 
                            max: maxReceivable,
                            style: { textAlign: 'center' }
                          }}
                        />
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              </Box>
            );
          })
        ) : (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
            {t(StringKeys.NO_ITEMS_TO_RECEIVE)}
          </Typography>
        )}
      </Stack>
    </BaseModal>
  );
};

export default ReceiveOrderModal;
